import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign, ShoppingCart, Package, Users, AlertTriangle,
  TrendingUp, TrendingDown, Clock, BarChart3, PieChart as PieChartIcon
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOLD = '#C9A962';
const GOLD_PALETTE = ['#C9A962', '#A68B4B', '#E0C97F', '#8B7340', '#F0DDA0', '#6B5A30', '#D4B86A', '#B89E55'];

const STATUS_MAP = {
  pending: { label: 'Pendiente', color: '#f97316' },
  confirmed: { label: 'Confirmado', color: '#3b82f6' },
  shipped: { label: 'Enviado', color: '#a855f7' },
  delivered: { label: 'Entregado', color: '#22c55e' },
  cancelled: { label: 'Cancelado', color: '#ef4444' },
};

const chartTooltipStyle = {
  contentStyle: {
    backgroundColor: '#1a1a1a',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '13px',
  },
  labelStyle: { color: 'rgba(255,255,255,0.6)' },
};

const formatCurrency = (val) => {
  if (val == null) return '$0.00';
  return `$${Number(val).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [chartDays, setChartDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/stats`);
        setStats(res.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    const fetchChart = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/sales-chart?days=${chartDays}`);
        setChartData(
          (res.data || []).map((d) => ({
            ...d,
            date: d._id,
            revenue: d.revenue || 0,
            orders: d.orders || 0,
          }))
        );
      } catch (error) {
        console.error('Error fetching chart:', error);
      }
    };
    fetchChart();
  }, [chartDays]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 skeleton rounded-none" />
          ))}
        </div>
        <div className="h-80 skeleton rounded-none" />
      </div>
    );
  }

  // Growth calculation
  const prevRevenue = stats?.prev_month_revenue || 0;
  const currRevenue = stats?.current_30d_revenue || 0;
  const growthPct = prevRevenue > 0 ? (((currRevenue - prevRevenue) / prevRevenue) * 100).toFixed(1) : null;

  const statCards = [
    {
      title: 'Ventas Hoy',
      value: formatCurrency(stats?.today?.revenue),
      subtitle: `${stats?.today?.orders || 0} pedidos`,
      icon: DollarSign,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    {
      title: 'Ventas Semana',
      value: formatCurrency(stats?.week?.revenue),
      subtitle: `${stats?.week?.orders || 0} pedidos`,
      icon: TrendingUp,
      color: 'text-gold',
      bgColor: 'bg-gold/10',
    },
    {
      title: 'Ventas Mes',
      value: formatCurrency(stats?.month?.revenue),
      subtitle: `${stats?.month?.orders || 0} pedidos`,
      icon: ShoppingCart,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      growth: growthPct,
    },
    {
      title: 'Ingresos Totales',
      value: formatCurrency(stats?.all_time_revenue),
      subtitle: `${stats?.total_orders || 0} pedidos totales`,
      icon: DollarSign,
      color: 'text-gold',
      bgColor: 'bg-gold/10',
    },
  ];

  // Order status data
  const orderStatusData = (stats?.order_status_breakdown || []).map((s) => ({
    status: STATUS_MAP[s._id]?.label || s._id,
    count: s.count,
    fill: STATUS_MAP[s._id]?.color || '#666',
  }));

  // Revenue by category data
  const categoryData = (stats?.revenue_by_category || []).map((c, i) => ({
    name: c._id || 'Sin categoría',
    revenue: c.revenue,
    count: c.count,
    fill: GOLD_PALETTE[i % GOLD_PALETTE.length],
  }));

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-serif text-white mb-2">Reportes</h1>
        <p className="text-white/60">Resumen completo de tu tienda</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-white/60 text-sm mb-1">{stat.title}</p>
                    <p className="text-2xl font-semibold text-white">{stat.value}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-white/40 text-sm">{stat.subtitle}</p>
                      {stat.growth != null && (
                        <span
                          className={`inline-flex items-center text-xs font-medium px-1.5 py-0.5 rounded ${
                            Number(stat.growth) >= 0
                              ? 'bg-green-500/10 text-green-500'
                              : 'bg-red-500/10 text-red-500'
                          }`}
                        >
                          {Number(stat.growth) >= 0 ? (
                            <TrendingUp className="w-3 h-3 mr-0.5" />
                          ) : (
                            <TrendingDown className="w-3 h-3 mr-0.5" />
                          )}
                          {stat.growth}%
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Sales Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card className="bg-[#121212] border-white/10">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white font-serif">Ventas Diarias</CardTitle>
            <div className="flex gap-1">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => setChartDays(d)}
                  className={`px-3 py-1 text-xs rounded transition-colors ${
                    chartDays === d
                      ? 'bg-gold text-black font-semibold'
                      : 'bg-white/5 text-white/60 hover:bg-white/10'
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis
                    dataKey="date"
                    stroke="rgba(255,255,255,0.3)"
                    tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                    tickFormatter={(v) => {
                      const parts = v?.split('-');
                      return parts ? `${parts[2]}/${parts[1]}` : v;
                    }}
                  />
                  <YAxis
                    stroke="rgba(255,255,255,0.3)"
                    tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                    tickFormatter={(v) => `$${v}`}
                  />
                  <Tooltip
                    {...chartTooltipStyle}
                    formatter={(value) => [formatCurrency(value), 'Ingresos']}
                    labelFormatter={(label) => `Fecha: ${label}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke={GOLD}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: GOLD }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Middle row: Order Status + Revenue by Category */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Order Status Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-serif flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-gold" />
                Pedidos por Estado
              </CardTitle>
            </CardHeader>
            <CardContent>
              {orderStatusData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={orderStatusData} layout="vertical" barCategoryGap="20%">
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                      <XAxis
                        type="number"
                        stroke="rgba(255,255,255,0.3)"
                        tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                      />
                      <YAxis
                        type="category"
                        dataKey="status"
                        stroke="rgba(255,255,255,0.3)"
                        tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 12 }}
                        width={90}
                      />
                      <Tooltip
                        {...chartTooltipStyle}
                        formatter={(value) => [`${value} pedidos`, 'Cantidad']}
                      />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                        {orderStatusData.map((entry, i) => (
                          <Cell key={i} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-white/40 text-center py-8">No hay datos de pedidos</p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Revenue by Category */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-serif flex items-center gap-2">
                <PieChartIcon className="w-5 h-5 text-gold" />
                Ventas por Categoría
              </CardTitle>
            </CardHeader>
            <CardContent>
              {categoryData.length > 0 ? (
                <div className="flex items-center gap-4">
                  <div className="h-64 flex-1">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={categoryData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={90}
                          paddingAngle={2}
                          dataKey="revenue"
                        >
                          {categoryData.map((entry, i) => (
                            <Cell key={i} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip
                          {...chartTooltipStyle}
                          formatter={(value) => [formatCurrency(value), 'Ingresos']}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-2 min-w-[140px]">
                    {categoryData.slice(0, 6).map((c, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span
                          className="w-3 h-3 rounded-sm flex-shrink-0"
                          style={{ backgroundColor: c.fill }}
                        />
                        <span className="text-white/70 truncate">{c.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-white/40 text-center py-8">No hay datos de categorías</p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Bottom row: Recent Orders + Low Stock */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-serif">Pedidos Recientes</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.recent_orders?.length > 0 ? (
                <div className="space-y-4">
                  {stats.recent_orders.map((order) => (
                    <div
                      key={order.id || order.order_number}
                      className="flex items-center justify-between py-3 border-b border-white/5 last:border-0"
                    >
                      <div>
                        <button
                          onClick={() => navigate('/admin/pedidos')}
                          className="text-gold hover:text-gold/80 font-medium transition-colors text-left"
                        >
                          {order.order_number}
                        </button>
                        <p className="text-white/60 text-sm">{order.customer_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-gold font-semibold">{formatCurrency(order.total)}</p>
                        <p
                          className={`text-xs ${
                            STATUS_MAP[order.order_status]?.color
                              ? ''
                              : 'text-white/40'
                          }`}
                          style={{
                            color: STATUS_MAP[order.order_status]?.color || undefined,
                          }}
                        >
                          {STATUS_MAP[order.order_status]?.label || order.order_status}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-white/40 text-center py-8">No hay pedidos recientes</p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Low Stock Products */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-serif flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Productos con Stock Bajo
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.low_stock_products?.length > 0 ? (
                <div className="space-y-3">
                  {stats.low_stock_products.map((product, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"
                    >
                      <p className="text-white text-sm truncate flex-1 mr-4">{product.name}</p>
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold ${
                          product.total_stock === 0
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-orange-500/20 text-orange-400'
                        }`}
                      >
                        {product.total_stock === 0 ? 'Sin stock' : `${product.total_stock} uds`}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-white/40 text-center py-8">No hay productos con stock bajo</p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Secondary stats pills */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="flex flex-wrap gap-3"
      >
        <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#121212] border border-white/10 rounded-full text-sm text-white/70">
          <Package className="w-4 h-4 text-white/40" />
          {stats?.total_products || 0} productos
        </span>
        <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#121212] border border-white/10 rounded-full text-sm text-white/70">
          <Users className="w-4 h-4 text-white/40" />
          {stats?.total_customers || 0} clientes
        </span>
        <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#121212] border border-white/10 rounded-full text-sm text-white/70">
          <Clock className="w-4 h-4 text-orange-500" />
          {stats?.pending_orders || 0} pedidos pendientes
        </span>
        <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#121212] border border-white/10 rounded-full text-sm text-white/70">
          <AlertTriangle className="w-4 h-4 text-red-500" />
          {stats?.low_stock_count || 0} productos stock bajo
        </span>
      </motion.div>
    </div>
  );
}
